import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Lightbulb,
  AlertTriangle,
  StickyNote,
  CheckSquare,
  Loader2,
  ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { toDateInputValue } from '../../../utils/formatters'
import EntryHistoryPanel from './EntryHistoryPanel'

export type AgendaEntryKind = 'NOTE' | 'DECISION' | 'RISK' | 'TASK'

export type AgendaEntryValue =
  | {
      entityType: 'subItem'
      id: string
      title: string
      notes?: string | null
      kind: 'NOTE' | 'DECISION' | 'RISK'
      status: string
      dueDate?: string | null
      assignedTo?: { id: string; name: string } | null
      orderIndex: number
      seriesId?: string | null
      previousVersionId?: string | null
      lineageId?: string | null
      lockedAt?: string | null
    }
  | {
      entityType: 'actionItem'
      id: string
      title: string
      description?: string | null
      kind: 'TASK'
      status: string
      dueDate?: string | null
      assignedTo?: { id: string; name: string } | null
      agendaOrderIndex: number
      originOccurrenceNumber?: number | null
      lineageId?: string | null
    }

export interface AgendaEntryPatch {
  title?: string
  notes?: string | null
  description?: string | null
  status?: string
  dueDate?: string | null
  assignedTo?: { id: string; name: string } | null
}

interface Props {
  value: AgendaEntryValue
  users: { id: string; name: string }[]
  inputRef?: React.MutableRefObject<HTMLInputElement | null>
  swapping?: boolean
  // canEdit gates the editable fields (title/kind/status/assignee/dueDate) —
  // all backed by updateAgendaSubItem/updateActionItem/promoteSubItemToActionItem/
  // demoteActionItemToSubItem, which are all MEETINGS.EDIT in the registry.
  canEdit: boolean
  // canDelete only applies when this entry is an actionItem (deleteActionItem is
  // MEETINGS.DELETE); subItem deletion (removeAgendaSubItem) is EDIT, so it's
  // gated by canEdit instead — see canDeleteThis below.
  canDelete: boolean
  onPatch: (patch: AgendaEntryPatch) => void
  onKindChange: (newKind: AgendaEntryKind) => void
  onDelete: () => void
}

const KIND_ICONS: Record<AgendaEntryKind, any> = {
  NOTE: StickyNote,
  DECISION: Lightbulb,
  RISK: AlertTriangle,
  TASK: CheckSquare,
}

const STATUS_ICONS: Record<string, any> = {
  OPEN: AlertCircle,
  IN_PROGRESS: Clock,
  RESOLVED: CheckCircle2,
  CLOSED: XCircle,
}

const KIND_STYLES: Record<AgendaEntryKind, string> = {
  NOTE: 'border-gray-200 bg-gray-50',
  DECISION: 'border-blue-200 bg-blue-50',
  RISK: 'border-red-200 bg-red-50',
  TASK: 'border-amber-200 bg-amber-50',
}

const KIND_TEXT: Record<AgendaEntryKind, string> = {
  NOTE: 'text-gray-600',
  DECISION: 'text-blue-600',
  RISK: 'text-red-600',
  TASK: 'text-amber-600',
}

const STATUS_TEXT: Record<string, string> = {
  OPEN: 'text-gray-500',
  IN_PROGRESS: 'text-blue-600',
  RESOLVED: 'text-green-600',
  CLOSED: 'text-red-600',
}

const KIND_OPTIONS: { value: AgendaEntryKind; labelKey: string }[] = [
  { value: 'NOTE', labelKey: 'AgendaSubItem_kind_note' },
  { value: 'DECISION', labelKey: 'AgendaSubItem_kind_decision' },
  { value: 'RISK', labelKey: 'AgendaSubItem_kind_risk' },
  { value: 'TASK', labelKey: 'AgendaSubItem_kind_task' },
]

const STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'OPEN', labelKey: 'ActionItem_status_open' },
  { value: 'IN_PROGRESS', labelKey: 'ActionItem_status_in_progress' },
  { value: 'RESOLVED', labelKey: 'ActionItem_status_resolved' },
  { value: 'CLOSED', labelKey: 'ActionItem_status_closed' },
]

const AgendaEntryRow: React.FC<Props> = ({
  value,
  users,
  inputRef: externalInputRef,
  swapping = false,
  canEdit,
  canDelete,
  onPatch,
  onKindChange,
  onDelete,
}) => {
  const { t } = useTranslation()
  const localInputRef = useRef<HTMLInputElement>(null)
  const setInputRef = (el: HTMLInputElement | null) => {
    localInputRef.current = el
    if (externalInputRef) externalInputRef.current = el
  }
  const [title, setTitle] = useState(value.title)
  const titleRef = useRef(value.title)

  useEffect(() => {
    if (document.activeElement === localInputRef.current) return
    setTitle(value.title)
    titleRef.current = value.title
  }, [value.title])

  const KindIcon = KIND_ICONS[value.kind] || StickyNote
  const StatusIcon = STATUS_ICONS[value.status] || AlertCircle

  const commitTitle = () => {
    if (title !== titleRef.current) {
      onPatch({ title })
      titleRef.current = title
    }
  }

  const disabled = swapping
  // Editable fields (title/kind/status/assignee/dueDate) require MEETINGS.EDIT.
  const fieldsDisabled = swapping || !canEdit
  // Deleting a subItem is EDIT (removeAgendaSubItem); deleting an actionItem
  // (a promoted TASK entry) is DELETE (deleteActionItem) — gate accordingly.
  const canDeleteThis = value.entityType === 'actionItem' ? canDelete : canEdit
  const deleteDisabled = swapping || !canDeleteThis

  const [expanded, setExpanded] = useState(false)

  const showHistoryToggle = !!value.lineageId

  return (
    <div
      className={cn(
        'rounded-md border p-2 relative',
        KIND_STYLES[value.kind] || '',
        disabled && 'opacity-60 pointer-events-none'
      )}
    >
      <div className="flex items-start gap-2">
      <KindIcon className={cn('h-4 w-4 mt-2 shrink-0', KIND_TEXT[value.kind])} />

      <div className="flex-1 min-w-0 space-y-2">
        <Input
          ref={setInputRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={commitTitle}
          placeholder={t('AgendaSubItem_title_placeholder')}
          className="h-8 text-sm bg-card"
          disabled={fieldsDisabled}
        />

        <div className="flex items-center gap-2">
          <Select
            value={value.kind}
            onValueChange={(v) => onKindChange(v as AgendaEntryKind)}
            disabled={fieldsDisabled}
          >
            <SelectTrigger className="h-7 flex-1 min-w-0 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KIND_OPTIONS.map((opt) => {
                const Icon = KIND_ICONS[opt.value]
                return (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    className="pl-2 [&>span:first-child]:hidden"
                  >
                    <span className="flex items-center gap-2">
                      <Icon className={cn('h-3.5 w-3.5 shrink-0', KIND_TEXT[opt.value])} />
                      {t(opt.labelKey)}
                    </span>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>

          <Select
            value={value.status}
            onValueChange={(v) => onPatch({ status: v })}
            disabled={fieldsDisabled}
          >
            <SelectTrigger className="h-7 flex-1 min-w-0 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => {
                const Icon = STATUS_ICONS[opt.value]
                return (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    className="pl-2 [&>span:first-child]:hidden"
                  >
                    <span className="flex items-center gap-2">
                      <Icon className={cn('h-3.5 w-3.5 shrink-0', STATUS_TEXT[opt.value])} />
                      {t(opt.labelKey)}
                    </span>
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>

          <Select
            value={value.assignedTo?.id || '__none__'}
            onValueChange={(v) =>
              onPatch({
                assignedTo:
                  v === '__none__'
                    ? null
                    : ({ id: v, name: users.find((u) => u.id === v)?.name || '' } as any),
              })
            }
            disabled={fieldsDisabled}
          >
            <SelectTrigger className="h-7 flex-1 min-w-0 text-xs">
              <SelectValue placeholder={t('AgendaSubItem_assignee_placeholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">—</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="date"
            value={toDateInputValue(value.dueDate)}
            onChange={(e) =>
              onPatch({
                dueDate: e.target.value
                  ? new Date(e.target.value).toISOString()
                  : null,
              })
            }
            className="h-7 w-[160px] shrink-0 text-xs bg-card"
            disabled={fieldsDisabled}
          />
        </div>
      </div>

      {showHistoryToggle && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          title={t('AgendaEntry_history_open', { defaultValue: 'Zobrazit historii' })}
          disabled={disabled}
          aria-expanded={expanded}
        >
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')}
          />
        </Button>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        title={t('Common_delete')}
        disabled={deleteDisabled}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      </div>

      {swapping && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-md">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {expanded && value.lineageId && (
        <div className="mt-3 pt-3 border-t border-dashed">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
            {t('AgendaEntry_history_label', { defaultValue: 'Historie verzí' })}
          </div>
          <EntryHistoryPanel lineageId={value.lineageId} />
        </div>
      )}
    </div>
  )
}

export default AgendaEntryRow
